import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bus, ChevronDown, Loader2, RefreshCw, Send, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { formatTripPrice, priceOfView } from '@/lib/transport/price';
import type { HotelTransferStrip } from '@/components/hotels/types';
import {
  transferBoardKnown,
  type ITransferBoard,
  type ShuttleRunView,
  type TransferVehicleClass,
} from '@/lib/disruptions/types';
import {
  useDispatchTransfers,
  usePlanTransfers,
  useTransferRunAction,
} from '@/lib/disruptions/use-disruptions';
import { shortTime, type TransferLens } from './tower-model';
import {
  RUN_NOT_DEPARTED,
  RUN_ORDER,
  boardTotals,
  runStatusLabel,
  runTone,
  runsOfHotel,
  vehicleLabel,
} from './tower-transfer-board';

// MASS ground transport — the plan editor of the Hotels view: vehicle classes,
// per-hotel strip (pax to move · vehicles · runs by phase · next pickup) with the
// per-hotel actions (Send to this hotel · Re-cut · Re-dispatch no-shows), and the
// run list (seat counts, plate, pickup, cancel until departed). Reads ONE query
// (['disruptions', urn, 'transfers']) that the WS keeps live; every write is a
// mutation whose echo lands over the same push. English copy, pax first.

const DEFAULT_CLASSES: TransferVehicleClass[] = [
  { code: 'BUS', capacity: 45 },
  { code: 'VAN', capacity: 12 },
  { code: 'CAR', capacity: 3 },
];
// Audit REASON strings travel to the API and land in the run's record — they are
// payload, not UI copy, so they stay in one language on purpose.
const DISPATCH_REASON = 'Mass transfer to hotels (Tower)';

/** i18n keys — this module holds keys, never language. */
const LENS_LABEL_KEY: Record<TransferLens, string> = {
  PLAN: 'transfer.lens.PLAN',
  WAITING: 'transfer.lens.WAITING',
  EN_ROUTE: 'transfer.lens.EN_ROUTE',
  NO_SHOW: 'transfer.lens.NO_SHOW',
  FAILED: 'transfer.lens.FAILED',
  RETURN: 'transfer.lens.RETURN',
};

export interface TransferPanelProps {
  disruptionUrn: string;
  board: ITransferBoard | null | undefined;
  isLoading: boolean;
  isError: boolean;
  strips: Map<string, HotelTransferStrip>;
  lens?: TransferLens | undefined;
  focusHotel?: string | undefined;
  /** transport-desk (T3): hide the plan-wide header actions (vehicle classes ·
   *  Plan/Re-cut · Send all) — the hosting tab's mass bar owns them, and the
   *  "Both legs" view mounts a second (return) instance that must not repeat
   *  them. Per-hotel + per-run actions stay. Default true (Tower unchanged). */
  headerActions?: boolean | undefined;
  /** transport-desk (T4): "open run sheet" affordance on each run row. Absent
   *  (the Tower default) ⇒ no affordance, rows unchanged. */
  onOpenRun?: ((run: ShuttleRunView) => void) | undefined;
  /** transport-desk (T4): "Add vehicle…" in the per-hotel action cluster. Absent
   *  (the Tower default) ⇒ no button. */
  onAddVehicle?: ((hotelUrn: string) => void) | undefined;
  /** Issue #662: Airport for formatting ground times in the correct timezone. */
  groundAirportIata?: string | null;
}

export function TransferPanel({
  disruptionUrn,
  board,
  isLoading,
  isError,
  strips,
  lens,
  focusHotel,
  headerActions = true,
  onOpenRun,
  onAddVehicle,
  groundAirportIata = null,
}: TransferPanelProps) {
  const { t } = useTranslation('tower');
  const plan = usePlanTransfers(disruptionUrn);
  const dispatch = useDispatchTransfers(disruptionUrn);
  const runAction = useTransferRunAction(disruptionUrn);
  const [classes, setClasses] = useState<TransferVehicleClass[]>(
    board?.plan?.vehicleClasses?.length ? board.plan.vehicleClasses : DEFAULT_CLASSES,
  );
  const [editClasses, setEditClasses] = useState(false);
  const [openHotel, setOpenHotel] = useState<string | null>(focusHotel ?? null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (board?.plan?.vehicleClasses?.length) setClasses(board.plan.vehicleClasses);
  }, [board?.plan?.vehicleClasses]);
  useEffect(() => {
    if (focusHotel) setOpenHotel(focusHotel);
  }, [focusHotel]);

  // The leg this instance draws. The "Both legs" view mounts the panel twice over
  // the SAME board, so every count, label and per-hotel action below has to be
  // asked of one leg — otherwise the return board repeats the outbound's numbers.
  const leg: ShuttleRunView['leg'] = lens === 'RETURN' ? 'HOTEL_TO_AIRPORT' : 'AIRPORT_TO_HOTEL';
  const returning = leg === 'HOTEL_TO_AIRPORT';
  const totals = useMemo(() => boardTotals(strips.values()), [strips]);
  const busy = plan.isPending || dispatch.isPending || runAction.isPending;
  const hasPlan = !!board?.plan;
  const runs = board?.runs ?? [];

  const fail = (what: string) => (err: Error) =>
    setError(t('transfer.action.failed', { what, message: err.message }));
  const planAll = () => {
    setError(null);
    plan.mutate({ vehicleClasses: classes }, { onError: fail(t('transfer.action.plan')) });
  };
  const planHotel = (hotelUrn: string) => {
    setError(null);
    plan.mutate(
      { vehicleClasses: classes, hotelUrn },
      { onError: fail(t('transfer.action.recut')) },
    );
  };
  const sendAll = () => {
    setError(null);
    dispatch.mutate({ reason: DISPATCH_REASON }, { onError: fail(t('transfer.action.dispatch')) });
  };
  const sendHotel = (hotelUrn: string) => {
    setError(null);
    dispatch.mutate(
      { hotelUrn, reason: DISPATCH_REASON },
      { onError: fail(t('transfer.action.dispatch')) },
    );
  };
  const redispatch = (hotelUrn: string) => {
    setError(null);
    const withNoShows = runsOfHotel(runs, hotelUrn, leg).filter(
      (r) => r.noShowCount > 0 && (r.status === 'DEPARTED' || r.status === 'ARRIVED'),
    );
    for (const r of withNoShows)
      runAction.mutate(
        { runUrn: r.urn, action: 'replace-no-shows', reason: 'Re-dispatch no-shows' },
        { onError: fail(t('transfer.action.redispatch')) },
      );
  };
  const cancelRun = (run: ShuttleRunView) => {
    setError(null);
    runAction.mutate(
      { runUrn: run.urn, action: 'cancel', reason: 'Cancelled by the operator (Tower)' },
      { onError: fail(t('transfer.action.cancel')) },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground" data-testid="transfer-panel-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{' '}
        {t('transfer.panel.loading')}
      </div>
    );
  }
  if (isError) {
    return (
      <p className="py-3 text-xs text-destructive" data-testid="transfer-panel-error">
        {t('transfer.panel.error')}
      </p>
    );
  }
  if (board === null) {
    return (
      <p className="py-3 text-xs text-muted-foreground" data-testid="transfer-panel-unavailable">
        {t('transfer.panel.unavailable')}
      </p>
    );
  }
  if (!board) return null;

  const hotelList = [...strips.values()];
  const visibleHotels =
    lens === 'RETURN'
      ? hotelList.filter((h) => h.returnRuns.scheduled + h.returnRuns.onRoad + h.returnRuns.done > 0 || h.paxArrived > 0)
      : lens === 'NO_SHOW'
        ? hotelList.filter((h) => h.noShow > 0)
        : lens === 'FAILED'
          ? hotelList.filter((h) => h.runs.failed > 0)
          : hotelList;

  return (
    <div className="rounded-lg border border-border" data-testid="transfer-panel">
      {/* Header: totals + plan-wide actions */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-3 py-2 text-sm">
        <Bus className="h-4 w-4 text-primary" aria-hidden />
        <span className="font-medium">
          {returning ? t('transfer.panel.titleReturn') : t('transfer.panel.title')}
        </span>
        <span className="text-xs text-muted-foreground" data-testid="transfer-panel-totals">
          {t('transfer.totals.lead', {
            pax: totals.paxToMove.toLocaleString(),
            hotels: t('count.hotels', { count: totals.hotels }),
            vehicles: t('count.vehicles', { count: totals.vehiclesPlanned }),
          })}
          <b className="font-semibold text-foreground">{totals.paxArrived.toLocaleString()}</b>{' '}
          {returning ? t('transfer.totals.atAirport') : t('transfer.totals.atHotel')}
          {t('transfer.totals.tail', {
            onRoad: totals.paxOnRoad.toLocaleString(),
            vehicles: t('count.vehicles', { count: totals.vehiclesOnRoad }),
            waiting: totals.paxWaiting.toLocaleString(),
          })}
          {totals.noShow > 0 ? (
            <span className="text-rose-700 dark:text-rose-300">
              {t('transfer.totals.noShow', { count: totals.noShow })}
            </span>
          ) : null}
          {totals.failed > 0 ? (
            <span className="text-amber-700 dark:text-amber-300">
              {t('transfer.totals.failed', { count: totals.failed })}
            </span>
          ) : null}
        </span>
        {lens ? (
          <Badge variant="info" size="sm" data-testid="transfer-panel-lens">
            {t('transfer.panel.lens', { lens: t(LENS_LABEL_KEY[lens]) })}
          </Badge>
        ) : null}
        {!transferBoardKnown(board) ? (
          <Badge variant="warning" size="sm">
            {t('transfer.panel.notRead')}
          </Badge>
        ) : null}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 px-2 text-xs"
            onClick={() => setEditClasses((v) => !v)}
            aria-expanded={editClasses}
            data-testid="transfer-classes-toggle"
          >
            {t('transfer.classes.toggle', {
              list: classes.map((c) => `${c.code} ${c.capacity}`).join(' / '),
            })}
            <ChevronDown className={cn('h-3 w-3 transition-transform', editClasses ? '' : '-rotate-90')} aria-hidden />
          </Button>
          {headerActions ? (
          <Button
            size="sm"
            variant={hasPlan ? 'outline' : 'default'}
            className="h-7 gap-1 px-2 text-xs"
            disabled={busy}
            onClick={planAll}
            data-testid="transfer-plan-all"
            title={hasPlan ? t('transfer.plan.recutTitle') : t('transfer.plan.planTitle')}
          >
            {plan.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <RefreshCw className="h-3 w-3" aria-hidden />}
            {hasPlan ? t('transfer.plan.recutAll') : t('transfer.plan.planShuttles')}
          </Button>
          ) : null}
          {headerActions && hasPlan ? (
            <Button
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              disabled={busy || totals.paxWaiting === 0}
              onClick={sendAll}
              data-testid="transfer-send-all"
              title={t('transfer.sendAll.title')}
            >
              {dispatch.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Send className="h-3 w-3" aria-hidden />}
              {t('transfer.sendAll.label', { pax: totals.paxWaiting.toLocaleString() })}
            </Button>
          ) : null}
        </div>
      </div>

      {editClasses ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-xs" data-testid="transfer-classes-editor">
          <span className="text-muted-foreground">{t('transfer.classes.cutWith')}</span>
          {DEFAULT_CLASSES.map((d) => {
            const on = classes.find((c) => c.code === d.code);
            return (
              <label key={d.code} className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1">
                <input
                  type="checkbox"
                  checked={!!on}
                  onChange={(e) =>
                    setClasses((prev) =>
                      e.target.checked
                        ? [...prev.filter((c) => c.code !== d.code), { ...d }].sort((a, b) => b.capacity - a.capacity)
                        : prev.filter((c) => c.code !== d.code),
                    )
                  }
                  aria-label={t('transfer.classes.runsAria', { code: d.code })}
                />
                <span className="font-medium">{d.code}</span>
                <input
                  type="number"
                  min={1}
                  max={80}
                  value={on?.capacity ?? d.capacity}
                  disabled={!on}
                  onChange={(e) => {
                    const cap = Math.max(1, Math.min(80, Number(e.target.value) || d.capacity));
                    setClasses((prev) => prev.map((c) => (c.code === d.code ? { ...c, capacity: cap } : c)));
                  }}
                  className="w-12 rounded border border-border bg-background px-1 py-0.5 text-right tabular-nums"
                  aria-label={t('transfer.classes.capacityAria', { code: d.code })}
                />
                <span className="text-muted-foreground">{t('transfer.classes.seats')}</span>
              </label>
            );
          })}
          <span className="text-muted-foreground">{t('transfer.classes.note')}</span>
        </div>
      ) : null}

      {error ? (
        <p className="border-b border-border px-3 py-1.5 text-xs text-destructive" data-testid="transfer-panel-error-line">
          {error}
        </p>
      ) : null}

      {/* Per-hotel rows */}
      {visibleHotels.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground" data-testid="transfer-panel-empty">
          {hotelList.length === 0
            ? t('transfer.empty.noHotel')
            : t('transfer.empty.lens', {
                lens: lens ? t(LENS_LABEL_KEY[lens]) : t('transfer.lens.PLAN'),
              })}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {visibleHotels.map((h) => {
            const open = openHotel === h.hotelUrn;
            const list = runsOfHotel(runs, h.hotelUrn, leg)
              .filter((r) =>
                lens === 'EN_ROUTE'
                  ? r.status === 'REQUESTED' || r.status === 'VEHICLE_ASSIGNED' || r.status === 'BOARDING' || r.status === 'DEPARTED'
                  : lens === 'NO_SHOW'
                    ? r.noShowCount > 0
                    : lens === 'FAILED'
                      ? r.status === 'FAILED'
                      : lens === 'WAITING'
                        ? r.status === 'PLANNED'
                        : true,
              )
              .sort((a, b) => RUN_ORDER.indexOf(a.status) - RUN_ORDER.indexOf(b.status) || a.seq - b.seq);
            return (
              <li key={h.hotelUrn} data-testid="transfer-hotel-row" data-hotel={h.hotelUrn}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2 text-xs">
                  <button
                    type="button"
                    className="inline-flex min-w-0 items-center gap-1.5 text-left"
                    onClick={() => setOpenHotel(open ? null : h.hotelUrn)}
                    aria-expanded={open}
                  >
                    <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open ? '' : '-rotate-90')} aria-hidden />
                    <span className="truncate font-semibold text-foreground">{h.hotelName}</span>
                  </button>
                  <TransferStrip strip={h} compact groundAirportIata={groundAirportIata} />
                  <div className="ml-auto flex flex-wrap items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 gap-1 px-2 text-[11px]"
                      disabled={busy || h.paxWaiting === 0}
                      onClick={() => sendHotel(h.hotelUrn)}
                      data-testid="transfer-send-hotel"
                      title={
                        returning
                          ? t('transfer.hotel.sendReturnTitle')
                          : t('transfer.hotel.sendTitle')
                      }
                    >
                      <Send className="h-3 w-3" aria-hidden />{' '}
                      {returning ? t('transfer.hotel.sendReturn') : t('transfer.hotel.send')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 gap-1 px-2 text-[11px]"
                      disabled={busy}
                      onClick={() => planHotel(h.hotelUrn)}
                      data-testid="transfer-recut-hotel"
                      title={t('transfer.hotel.recutTitle')}
                    >
                      <RefreshCw className="h-3 w-3" aria-hidden /> {t('transfer.hotel.recut')}
                    </Button>
                    {h.noShow > 0 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 gap-1 px-2 text-[11px] text-rose-700 dark:text-rose-300"
                        disabled={busy}
                        onClick={() => redispatch(h.hotelUrn)}
                        data-testid="transfer-redispatch-hotel"
                        title={t('transfer.hotel.redispatchTitle')}
                      >
                        {t('transfer.hotel.redispatch', {
                          noShows: t('count.noShows', { count: h.noShow }),
                        })}
                      </Button>
                    ) : null}
                    {onAddVehicle ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 gap-1 px-2 text-[11px]"
                        disabled={busy}
                        onClick={() => onAddVehicle(h.hotelUrn)}
                        data-testid="transfer-add-vehicle"
                        title={t('transfer.hotel.addVehicleTitle')}
                      >
                        {t('transfer.hotel.addVehicle')}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {open ? (
                  <div className="border-t border-border bg-muted/20 px-3 py-2">
                    <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <span>
                        {t('transfer.hotel.pickup')}{' '}
                        <b className="font-medium text-foreground">
                          {h.pickupPoint ?? t('transfer.hotel.pickupDefault')}
                        </b>
                      </span>
                      {h.nextPickupAt ? (
                        <span>{t('transfer.hotel.next', { time: shortTime(h.nextPickupAt, groundAirportIata) })}</span>
                      ) : null}
                      {returning ? null : (
                        <span>
                          {t('transfer.hotel.return', {
                            done: h.returnRuns.done,
                            onRoad: h.returnRuns.onRoad,
                            scheduled: h.returnRuns.scheduled,
                          })}
                        </span>
                      )}
                    </div>
                    {list.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground" data-testid="transfer-runs-empty">
                        {lens && lens !== 'PLAN'
                          ? t('transfer.runs.emptyLens')
                          : t('transfer.runs.empty')}
                      </p>
                    ) : (
                      <ul className="grid gap-1" data-testid="transfer-run-list">
                        {list.map((r) => (
                          <RunRow
                            key={r.urn}
                            run={r}
                            busy={busy}
                            onCancel={() => cancelRun(r)}
                            onOpen={onOpenRun ? () => onOpenRun(r) : undefined}
                            groundAirportIata={groundAirportIata}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RunRow({
  run,
  busy,
  onCancel,
  onOpen,
  groundAirportIata,
}: {
  run: ShuttleRunView;
  busy: boolean;
  onCancel: () => void;
  onOpen?: (() => void) | undefined;
  /** Issue #662: Airport for formatting ground times in the correct timezone. */
  groundAirportIata: string | null;
}) {
  const { t, i18n } = useTranslation('tower');
  const tone = runTone(run.status);
  // The airline's price of the run (commercial-contract P5) — never Nexa's cost.
  const price = priceOfView(run);
  const seats = `${run.status === 'ARRIVED' ? run.arrivedCount || run.boardedCount : run.status === 'BOARDING' || run.status === 'DEPARTED' ? run.boardedCount : run.assignedCount}/${run.capacity}`;
  return (
    <li
      className="grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded-md border border-border bg-background px-2 py-1 text-[11px]"
      data-testid="transfer-run"
      data-status={run.status}
    >
      {onOpen ? (
        <button
          type="button"
          className="font-mono font-semibold text-primary hover:underline"
          onClick={onOpen}
          title={t('transfer.run.openSheet')}
          data-testid="transfer-run-open"
        >
          #{run.seq}
        </button>
      ) : (
        <span className="font-mono font-semibold">#{run.seq}</span>
      )}
      <span className="min-w-0 truncate">
        <span className="font-medium">{vehicleLabel(run)}</span>
        <span className="text-muted-foreground">{t('transfer.run.seats', { seats })}</span>
        {run.vehicle?.plate ? <span className="text-muted-foreground"> · {run.vehicle.plate}</span> : null}
        <span className="text-muted-foreground">
          {t('transfer.run.pickup', { time: shortTime(run.scheduledAt, groundAirportIata) })}
        </span>
        {run.noShowCount > 0 ? (
          <span className="text-rose-700 dark:text-rose-300">
            {t('transfer.run.noShow', { count: run.noShowCount })}
          </span>
        ) : null}
        {run.lastError ? <span className="text-amber-700 dark:text-amber-300"> · {run.lastError}</span> : null}
        {price ? (
          <span className="text-muted-foreground" data-testid="transfer-run-price">
            {t('transfer.run.price', { amount: formatTripPrice(price, i18n.language) })}
          </span>
        ) : price === null ? (
          <span className="text-muted-foreground" data-testid="transfer-run-price">
            {t('transfer.run.priceUnavailable')}
          </span>
        ) : null}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          className={cn(
            'rounded-full px-1.5 py-px font-semibold',
            tone === 'ok' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
            tone === 'run' && 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
            tone === 'warn' && 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
            tone === 'danger' && 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
            tone === 'muted' && 'bg-muted text-muted-foreground',
          )}
        >
          {runStatusLabel(run.status, t)}
        </span>
        {RUN_NOT_DEPARTED.has(run.status) ? (
          <button
            type="button"
            className="inline-flex items-center gap-0.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
            disabled={busy}
            onClick={onCancel}
            title={t('transfer.run.cancelTitle')}
            data-testid="transfer-run-cancel"
          >
            <X className="h-3 w-3" aria-hidden /> {t('common:common.cancel')}
          </button>
        ) : null}
      </span>
    </li>
  );
}

/** The 4-tone strip: pax to move · vehicles planned · runs dispatched / boarding /
 *  in transit / arrived · next pickup. Drawn on the hotel card and in the panel. */
export function TransferStrip({
  strip,
  compact = false,
  groundAirportIata = null,
}: {
  strip: HotelTransferStrip;
  compact?: boolean;
  /** Issue #662: Airport for formatting ground times in the correct timezone. */
  groundAirportIata?: string | null;
}) {
  const { t } = useTranslation('tower');
  const total = Math.max(1, strip.paxToMove);
  const pct = (n: number) => `${Math.max(0, Math.min(100, (n / total) * 100))}%`;
  const boarding = strip.runs.boarding;
  const active = strip.runs.dispatched + boarding + strip.runs.inTransit;
  return (
    <div className={cn('flex min-w-0 items-center gap-2', compact ? 'text-[11px]' : 'text-xs')} data-testid="transfer-strip">
      <Bus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="whitespace-nowrap text-muted-foreground">
        <b className="font-semibold text-foreground">{strip.paxArrived}</b>
        {t('transfer.strip.line', {
          total: strip.paxToMove,
          vehicles: t('count.vehicles', { count: strip.vehiclesPlanned }),
          dispatched: strip.runs.dispatched,
          boarding,
          inTransit: strip.runs.inTransit,
          arrived: strip.runs.arrived,
        })}
        {strip.runs.failed > 0 ? (
          <span className="text-amber-700 dark:text-amber-300">
            {t('transfer.strip.failed', { count: strip.runs.failed })}
          </span>
        ) : null}
        {strip.noShow > 0 ? (
          <span className="text-rose-700 dark:text-rose-300">
            {t('transfer.strip.noShow', { count: strip.noShow })}
          </span>
        ) : null}
        {strip.nextPickupAt && active + strip.runs.planned > 0 ? (
          <span>{t('transfer.strip.nextPickup', { time: shortTime(strip.nextPickupAt, groundAirportIata) })}</span>
        ) : null}
      </span>
      <div
        className="flex h-1.5 w-28 shrink-0 overflow-hidden rounded-full bg-muted"
        aria-hidden
        title={t('transfer.strip.barTitle')}
      >
        <i className="block h-full" style={{ width: pct(strip.paxArrived), background: 'var(--status-ok, #1F8A5B)' }} />
        <i
          className="block h-full"
          style={{
            width: pct(strip.paxOnRoad),
            backgroundImage: 'repeating-linear-gradient(135deg, hsl(var(--primary)) 0 2px, transparent 2px 4px)',
          }}
        />
        <i className="block h-full" style={{ width: pct(strip.paxWaiting), background: 'var(--status-pending, #E8C98A)' }} />
        <i className="block h-full" style={{ width: pct(strip.noShow), background: 'var(--status-danger, #B42318)' }} />
      </div>
    </div>
  );
}
